import React from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight, Award, Target, Eye, CheckCircle,
  Factory, Utensils, Shirt, Beaker, Droplets, Zap,
  Leaf, Wind, Box, Snowflake, Package, Cog,
} from 'lucide-react';
import { useContent, useCompany } from '../context/ContentContext';

// Map industry name -> icon (case-insensitive contains match)
const INDUSTRY_ICONS = [
  { match: /oem/i,           Icon: Cog },
  { match: /food/i,          Icon: Utensils },
  { match: /textile/i,       Icon: Shirt },
  { match: /plastic/i,       Icon: Box },
  { match: /rubber/i,        Icon: Droplets },
  { match: /chemical|pharma/i, Icon: Beaker },
  { match: /electroplat/i,   Icon: Zap },
  { match: /fertilizer/i,    Icon: Leaf },
  { match: /pet/i,           Icon: Package },
  { match: /foam/i,          Icon: Wind },
  { match: /cooler|honey/i,  Icon: Snowflake },
  { match: /packag/i,        Icon: Package },
];
const iconFor = (name) => (INDUSTRY_ICONS.find((m) => m.match.test(name))?.Icon) || Factory;

export default function About() {
  const { site_content } = useContent();
  const companyInfo = useCompany();
  const industries = companyInfo.industries || [];
  const features = [
    { icon: Award, title: site_content?.about_feature_1_title || 'ISO Certified', desc: site_content?.about_feature_1_desc || '' },
    { icon: Target, title: site_content?.about_feature_2_title || 'Mission', desc: site_content?.about_feature_2_desc || '' },
    { icon: Eye, title: site_content?.about_feature_3_title || 'Vision', desc: site_content?.about_feature_3_desc || '' },
    { icon: CheckCircle, title: site_content?.about_feature_4_title || 'Quality', desc: site_content?.about_feature_4_desc || '' },
  ];
  return (
    <div>
      <PageBanner title={site_content?.page_about_title || 'About Us'} />
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-10 items-start">
          <div>
            <p className="font-display tracking-[4px] text-[#f7941e] text-xs mb-2 uppercase">{site_content?.about_subtitle || 'Our Story'}</p>
            <h2 className="font-display text-3xl font-bold uppercase text-gray-900 mb-4">{site_content?.about_heading || 'Leading Manufacturer of Heating Elements'}</h2>
            <div className="w-16 h-1 bg-[#f7941e] mb-5 rounded" />
            <p className="text-gray-600 leading-relaxed mb-3">{site_content?.about_p1 || ''}</p>
            <p className="text-gray-600 leading-relaxed mb-3">{site_content?.about_p2 || ''}</p>
            <p className="text-gray-600 leading-relaxed">{site_content?.about_p3 || ''}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {features.map((c) => (
              <div key={c.title} className="bg-[#fffaf0] border border-[#f4e6c6] rounded-lg p-5 hover:shadow-md hover:-translate-y-1 transition-all">
                <div className="w-12 h-12 rounded bg-[#f7941e]/10 flex items-center justify-center mb-3"><c.icon className="w-5 h-5 text-[#f7941e]" /></div>
                <h3 className="font-display uppercase tracking-wide text-gray-900 mb-1">{c.title}</h3>
                <p className="text-sm text-gray-600">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-16 bg-gradient-to-b from-[#fffaf0] to-white relative overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 20px 20px, #f7941e 2px, transparent 2px)', backgroundSize: '40px 40px' }} />
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="text-center mb-12">
            <p className="font-display tracking-[4px] text-[#f7941e] text-xs mb-2 uppercase">Our Reach</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold uppercase text-gray-900">Industries We Serve</h2>
            <div className="w-20 h-1 bg-[#f7941e] mx-auto mt-3 rounded" />
            <p className="text-gray-600 text-sm max-w-2xl mx-auto mt-4">
              Trusted across {industries.length}+ industries for our reliable heating solutions, custom designs and uncompromising quality.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {industries.map((ind) => {
              const Icon = iconFor(ind);
              return (
                <div
                  key={ind}
                  className="group relative bg-white border border-[#f4e6c6] rounded-xl p-5 text-center hover:border-[#f7941e] hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-[#fff7e6] to-[#fef0d3] flex items-center justify-center mb-3 group-hover:from-[#f7941e] group-hover:to-[#e67e00] transition-all duration-300">
                    <Icon className="w-6 h-6 text-[#f7941e] group-hover:text-white transition-colors duration-300" strokeWidth={1.8} />
                  </div>
                  <p className="font-display text-[13px] uppercase tracking-wide text-gray-800 leading-tight">{ind}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

export function PageBanner({ title }) {
  return (
    <div className="page-banner">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="font-display text-3xl md:text-4xl uppercase tracking-wider mb-2">{title}</h1>
        <div className="flex items-center gap-2 text-sm">
          <Link to="/" className="hover:underline">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <span>{title}</span>
        </div>
      </div>
    </div>
  );
}
